"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildEInvoice = void 0;
const model_1 = require("./model");
const config_1 = require("./config");
function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
function resolveRate(cat, rate) {
    if (typeof rate === 'number')
        return rate;
    return config_1.DEFAULT_RATE_FOR_CATEGORY[cat];
}
/**
 * Build a validated-shape EN16931 e-invoice from a normalised sale.
 *
 * VAT is calculated per category on the summed taxable amount (BG-23), which is
 * the EN16931-correct rounding point — not per line.
 */
function buildEInvoice(sale, opts) {
    var _a, _b, _c;
    const documentType = (_a = sale.documentType) !== null && _a !== void 0 ? _a : 'invoice';
    const currency = (_b = sale.currency) !== null && _b !== void 0 ? _b : config_1.DEFAULT_CURRENCY;
    const lines = sale.lines.map((l, i) => {
        var _a, _b, _c;
        const quantity = (_a = l.quantity) !== null && _a !== void 0 ? _a : 1;
        const category = (_b = l.vatCategory) !== null && _b !== void 0 ? _b : 'S';
        const rate = resolveRate(category, l.vatRate);
        const unitPriceExVat = sale.pricesIncludeVat
            ? l.unitPrice / (1 + rate / 100)
            : l.unitPrice;
        const lineNetAmount = round2(unitPriceExVat * quantity);
        return {
            id: String(i + 1),
            description: l.description,
            itemCode: l.itemCode,
            quantity,
            unitCode: (_c = l.unitCode) !== null && _c !== void 0 ? _c : config_1.DEFAULT_UNIT,
            unitPriceExVat: round2(unitPriceExVat),
            lineNetAmount,
            vatCategory: category,
            vatRate: rate,
        };
    });
    // Group taxable amounts by category+rate, then tax once per group.
    const groups = new Map();
    for (const line of lines) {
        const key = `${line.vatCategory}:${line.vatRate}`;
        const existing = groups.get(key);
        if (existing) {
            existing.taxableAmount = round2(existing.taxableAmount + line.lineNetAmount);
        }
        else {
            groups.set(key, {
                category: line.vatCategory,
                rate: line.vatRate,
                taxableAmount: line.lineNetAmount,
                taxAmount: 0,
            });
        }
    }
    const vatBreakdown = [...groups.values()].map((g) => ({
        ...g,
        taxAmount: round2((g.taxableAmount * g.rate) / 100),
    }));
    const lineExtensionAmount = round2(lines.reduce((s, l) => s + l.lineNetAmount, 0));
    const taxTotalAmount = round2(vatBreakdown.reduce((s, b) => s + b.taxAmount, 0));
    const taxInclusiveAmount = round2(lineExtensionAmount + taxTotalAmount);
    return {
        specificationId: config_1.SPEC_ID,
        profileId: config_1.PROFILE_ID,
        id: sale.invoiceNumber,
        issueDate: sale.issueDate,
        typeCode: model_1.TYPE_CODE[documentType],
        documentType,
        currency,
        note: sale.note,
        paymentReference: sale.paymentReference,
        seller: sale.seller,
        buyer: sale.buyer,
        lines,
        vatBreakdown,
        totals: {
            lineExtensionAmount,
            taxExclusiveAmount: lineExtensionAmount,
            taxTotalAmount,
            taxInclusiveAmount,
            payableAmount: taxInclusiveAmount,
        },
        clearance: { status: 'not-live' },
        meta: {
            generatedBy: opts.appId,
            kitVersion: config_1.KIT_VERSION,
            generatedAt: (_c = opts.generatedAt) !== null && _c !== void 0 ? _c : new Date().toISOString(),
            source: sale.source,
        },
    };
}
exports.buildEInvoice = buildEInvoice;
